import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from 'src/app/base/base.component';
import { DataConnectorType } from 'src/app/domainObjects/dataConnector';
import { DataConnectorMapping } from 'src/app/domainObjects/dataConnectorMapping';
import { DataSourceType, DataSourceTypeUtil } from 'src/app/domainObjects/dataSourceType';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ColumnMapDefinitionModel } from 'src/app/model/columnMapDefinitionModel';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { SaveMappingConfigDialogComponent } from 'src/app/views/save-mapping-config-dialog/save-mapping-config-dialog.component';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { ConfigViewModelService } from '../../../services/configViewModel/config-view-model.service';
import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { MatStepper } from '@angular/material/stepper';
import { EventBrokerService, EventData } from 'src/app/services/eventBroker/event-broker.service';

import { ColumnFilterInfo } from 'src/app/views/previewTable/filterUI/column-filter/column-filter.component';
import { GroupedTransformPipelineItem, TransformPipelineItem, TransformPipelineViewModel } from 'src/app/views/transform/transform-pipeline-dialog/transformPipelineViewModel';
import { TransformPipelineService } from 'src/app/services/transformPipeline/transform-pipeline.service';
import { TransformerViewModel } from 'src/app/views/transformer-list/transformer-list.component';
import { ColumnInfo, PreviewTableComponent } from 'src/app/views/preview-table/preview-table.component';
import { RowExtractorPreviewDataResponse } from 'src/app/messaging/rowExtractorPreviewDataResponse';
import { TransformDetailViewModelBase } from 'src/app/views/dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';

import { DataRefineryConfig } from 'src/app/domainObjects/dataRefineryConfig';
import { UserDataService } from 'src/app/services/user-data-service';
import { RefineryPresenceService } from 'src/app/services/refinery-presence.service';
import { NodedefinitionComponent } from 'src/app/nodedefinition/nodedefinition.component';
import { RunNodeDialogComponent } from 'src/app/nodedefinition/run-node-dialog/run-node-dialog.component';
import { SharedService } from 'src/app/services/shared.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog.service';
import { Subscription } from 'rxjs';
import { CacheKeyType, PreviewType } from 'src/app/domainObjects/transformType';
import { RunrefineryRequest } from 'src/app/model/RunrefineryRequest';
import _ from 'lodash';
import { RunrefineryResponse } from 'src/app/model/RunrefineryResponse';
import { CacheStatusRequest } from 'src/app/messaging/resetRefineryNodesStatusRequest';
import { TestWriterRequest } from '../../transform/export-output-data-dynamic/export-output-data-dynamic.component';
import { NodeRunStatus } from 'src/app/domainObjects/nodeRunStatus';
import { IngestConfigViewModel } from 'src/app/views/import-data/import-data.component';
import { JsonPasteDialogComponent } from 'src/app/views/shared/json-paste-dialog/json-paste-dialog.component';
import { Clipboard } from '@angular/cdk/clipboard';

export class SelectedTabChangedEvent extends EventData {
  tabIndex: number;

  constructor(index: number) {
    super();
    this.tabIndex = index;
  }
}

export enum ConfigWorkFlowStep {
  ingestStep,
  mapperStep,
  subTaskStep,
  taskStep,
  activityStep,
  transformStep,
  reviewStep
}

export class ConfigWorkFlowStepModel {
  step: ConfigWorkFlowStep;
  isComplete = false;
  name: string;
  colorClass?: string;

  constructor(step: ConfigWorkFlowStep, name: string, colorClass?: string) {
    this.step = step;
    this.name = name;
    this.colorClass = colorClass;
  }
}

export class ConfigDetailViewModel {
  config?: DataTransformerConfig;
  dataSourceType?: DataSourceType;
  isIngestStepComplete: boolean;
  isMapperStepComplete: boolean;
  configName?: string;
  dataTransformConfigViewModel: DataTransformConfigViewModel;
  columns: ColumnMapDefinitionModel[] = [];
  isTransformRunning = false;

  saveInProgress = false;

  steps: ConfigWorkFlowStepModel[] = [
    new ConfigWorkFlowStepModel(ConfigWorkFlowStep.ingestStep, 'Ingest', 'ingest-color'),
    new ConfigWorkFlowStepModel(ConfigWorkFlowStep.subTaskStep, 'Subtask Farm', 'subTask-color'),
    new ConfigWorkFlowStepModel(ConfigWorkFlowStep.taskStep, 'Task Farm', 'task-color'),
    new ConfigWorkFlowStepModel(ConfigWorkFlowStep.activityStep, 'Activity Farm', 'activity-color'),
    new ConfigWorkFlowStepModel(ConfigWorkFlowStep.reviewStep, 'Review'),
  ];

  constructor( config?: DataTransformerConfig ) {

    if (config) {
      this.config = config;
      this.configName = config.name;
      this.dataSourceType = config.dataSourceType;
      this.dataTransformConfigViewModel = new DataTransformConfigViewModel(config);
      this.dataTransformConfigViewModel.dataSourceType = this.dataSourceType;
    }
  }

  updateTransformConfigViewModel(vm: DataTransformConfigViewModel): void {
    this.dataTransformConfigViewModel = vm;
    this.config = vm.dataTransformerConfig;
    this.configName = vm.dataTransformerConfig?.name;
  }
}



@Component({
    selector: 'app-config-detail',
    templateUrl: './config-detail.component.html',
    styleUrls: ['./config-detail.component.css'],
    standalone: false
})

export class ConfigDetailComponent<TViewModel extends TransformDetailViewModelBase<any>> extends BaseComponent implements OnInit, AfterViewInit {


  @ViewChild('rootStepper') rootStepper: MatStepper;
  @ViewChild('previewTable') previewTable: PreviewTableComponent;
  @ViewChild('outputDataFarmPreviewTable') outputDataFarmPreviewTable: PreviewTableComponent;


  viewModelBase: TViewModel  ;
  viewModel: ConfigDetailViewModel;
  DataSourceType = DataSourceType;
  dataSourceTypeUtil = DataSourceTypeUtil;
  DataConnectorType = DataConnectorType;
  DataFarmType = DataFarmType;
  StepType = ConfigWorkFlowStep;
  isConfigRunning = false;
  transformViewModel: TransformerViewModel;
  isSaveIngestDataBtnClicked: boolean = false;
  selectedIngestFarmGUID: string = "";
  showMapnotsavedCnfrmDialog: boolean = false;
  private subscription!: Subscription;
  private eventBusSub: Subscription;
  private configVmSub: Subscription;
  
  input_File_Path?: string  = '';
  input_File_Separate?: string  = '';

  minDialogDisplayTimeMs = 1000;
  iconpath:string;
  public  DataConnectorforRun?:any ;
  public  allNewConfigAfterSave?:DataTransformerConfig ;
  public  allTransformerConfigforpipeline?:TransformerViewModel[] = [] ;
  public  runButtonText:string = 'RUN';
  public  saveButtonText:string = 'SAVE';
  public nodeViewModel?: any ;
  public  width:string = '100';
  cacheStatus:string="";
  
  eventBusObserver = {
    next: (x: EventData) => {
      try {
        if (x instanceof SelectedTabChangedEvent) {
          const index = (x as SelectedTabChangedEvent).tabIndex;
          this.rootStepper.selectedIndex = index;
        }
      }
      catch (err) {

      }
    },
    error: (err: Error) => console.error('Observer got an error: ' + err),
    complete: () => console.log('Observer got a complete notification'),
  };

  configViewModelObserver = {
    next: (x: DataTransformConfigViewModel) => {
      this.viewModel.updateTransformConfigViewModel(x);
    },
    error: (err: Error) => {
      console.error('Observer got an error: ' + err);
    },
    complete: () => {
      console.log('Observer got a complete notification');
    }
  };

  
  constructor(
    private transformPipelineService: TransformPipelineService,
    private formBuilder: UntypedFormBuilder,
    matDialog: MatDialog,
    private activeRoute: ActivatedRoute,
    private configService: ConfigService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    errorLogger: ErrorReportingService,
    private configViewModelService: ConfigViewModelService,
    public navService: NavigationService,
    private eventBroker: EventBrokerService,
    private sharedService: SharedService,
    private clipboard: Clipboard,
    private userData: UserDataService, private confirmDialogService: ConfirmDialogService,
    private refineryPresence: RefineryPresenceService) {
      super(errorLogger, matDialog);
     }

  refineryName:string;
  refineryConfigGuid:string;
  ingestSaveConfig:any;

  /** True whenever THIS tab does not currently hold the Edit lock for the
   * open refinery — mirrors BaseTransformerUiComponent.isReadOnly for the
   * Ingest/node-level config view. Gates Save/Reset/Paste/Import in the
   * template below; the backend independently rejects the underlying
   * calls regardless. */
  // See the identical comment on BaseTransformerUiComponent.isReadOnly —
  // 'none' must NOT default to Read-Only, or a brand-new refinery's first
  // Ingest node would incorrectly show Read-Only the moment it's opened.
  get isReadOnly(): boolean {
    return this.refineryPresence.mode === 'readOnly';
  }

  ngOnInit(): void {
    // Same reasoning as BaseTransformerUiComponent.ngOnInit(): a cold load
    // (refresh, or — the case that surfaced this — opening a brand-new
    // refinery's very first Ingest node right after creation, which never
    // goes through NodedefinitionComponent.tryAcquireEdit() at all) leaves
    // RefineryPresenceService.mode at its default 'none'. isReadOnly
    // correctly reads that as "not Read-Only" (see the getter above), but
    // looking editable isn't the same as actually holding the lock — the
    // backend's CheckEditLockAsync only authorizes a save if a presence
    // record was actually acquired for this (UserId, TabId). Without this,
    // Save Ingest 403'd every time for a freshly-created refinery, even
    // though the UI showed no Read-Only indicator at all.
    const coldLoadRefineryGuid = this.userData.getSelectedRefineryDetails()?.guid;
    if (this.refineryPresence.mode === 'none' && coldLoadRefineryGuid) {
      this.refineryPresence.requestEdit(coldLoadRefineryGuid).catch(err =>
        console.error('Failed to re-acquire refinery edit lock on cold load:', err)
      );
    }

    this.eventBusSub = this.eventBroker.eventBus.subscribe(this.eventBusObserver);

    this.activeRoute.params.subscribe(async routeParams => {
      
    });

    this.activeRoute.queryParams.subscribe(async queryParams => {
      
    });

    this.subscription = this.sharedService.getEvent().subscribe(data => {
      this.cdRef.detectChanges();
      if(data == "mapper_updated") {
        this.showMapnotsavedCnfrmDialog = true;
      } else {
        this.showMapnotsavedCnfrmDialog = false;
      }
      if (data === "get_node_status_update") {
        console.log("get_node_status_update");
        this.getCacheStatus();
      }
      // Ingest file browser "Set current path as Home" (FileBrowserDialogComponent).
      if (data?.type === 'set_ingest_home_path') {
        this.saveIngestHomePath(data.dataConnectorType, data.path);
      }
    });

    const state = history.state;
    const configJsonObject =this.userData.getSelectedConfigJson(); //state.config;
    const configGuid = configJsonObject?.guid;

    const selectedRconfig=this.userData.getSelectedRefineryDetails();
    this.refineryName=selectedRconfig.name;
    this.refineryConfigGuid=selectedRconfig.guid;

    if (configGuid) {
      // Root cause, 2026-10-01 (the Architect: Audit Compiler Quality's red/green state
      // reverted to blue after navigating via the "Node N: name" breadcrumb and back,
      // even though the server held the correct persisted result). userData.getSelectedConfigJson()
      // is a THIRD, independent snapshot store, written only by NodedefinitionComponent's
      // node-box click handler on the pipeline graph -- never refreshed by Save's compiled-state
      // update or by an Audit run. Rebuilding from it here and unconditionally overwriting
      // configViewModelService's live singleton (further below) silently discarded any
      // in-session change made since the Analyst last entered this node from the graph --
      // not Audit-specific, any future runtime mutation of the live config hits the same trap.
      // Fixed the same way BaseTransformerUiComponent.syncRouteContext() already treats this
      // exact tradeoff elsewhere: only fall back to the stored snapshot when the live singleton
      // doesn't already represent this same config; otherwise reuse the live instance as-is,
      // since it's already more current than anything a stale snapshot could reconstruct.
      const liveViewModel = this.configViewModelService.getDataTransformConfigViewModel();
      if (liveViewModel?.dataTransformerConfig?.guid === configGuid) {
        this.viewModel = new ConfigDetailViewModel(liveViewModel.dataTransformerConfig);
      } else {
        // TODO: Verify this, we pass in the instance not json in state
        var config: DataTransformerConfig = DataTransformerConfig.fromJSON(configJsonObject);
        config.dataSourceType = configJsonObject.dataSourceType ;
        this.viewModel = new ConfigDetailViewModel(config);
      }
    } else {
      const dataSourceType = configJsonObject?.dataSourceType ?? state.newConfigDataSourceType;
      this.viewModel = new ConfigDetailViewModel();
      this.viewModel.dataSourceType = dataSourceType;
      this.viewModel.dataTransformConfigViewModel = new DataTransformConfigViewModel();
      this.viewModel.dataTransformConfigViewModel.dataSourceType = dataSourceType;
    }
    this.configViewModelService.setDataTransformConfigViewModel(this.viewModel.dataTransformConfigViewModel);
    this.configVmSub = this.configViewModelService.configViewModel$.subscribe(this.configViewModelObserver);

    this.nodeViewModel=this.userData.getSelectedNodeDetails() ;
    this.viewModel.dataTransformConfigViewModel.name=this.nodeViewModel.name;
    if(this.nodeViewModel.nodeboxtypeid==1){
      this.iconpath=this.nodeViewModel.ingesticonpath;
    }else{
      this.iconpath=this.nodeViewModel.processoriconpath;
    }
    this.getCacheStatus();
  }
  ngOnDestroy() {
    this.subscription?.unsubscribe(); // Clean up to avoid memory leaks
    this.eventBusSub?.unsubscribe();
    this.configVmSub?.unsubscribe();
  }
  ngAfterViewInit(): void {
    const index = this.userData.getSelectedTabIndex();
    if (index) {
      this.rootStepper.selectedIndex = index;
    }
    if (this.rootStepper.selectedIndex == 0) {
      this.runButtonText = 'RUN INGEST';
      this.saveButtonText = 'Save & Ingest Data';
      this.width='105';
    }
    if (this.rootStepper.selectedIndex == 1) {
      this.runButtonText = 'RUN PROCESSOR';
      this.saveButtonText = 'Run Processor';
      this.width='150';
    }
    this.cdRef.detectChanges();
  }

  async runConfig(): Promise<void> {
    const busyIndicator = this.showBusyDialog('Running Config...');
    try {
      this.isConfigRunning = true;
    }catch (err) {
      this.displayError('Failed to run configuration', 'Error', err);
    } finally {
      this.isConfigRunning = false;
      busyIndicator.close();
    }
  }

  onRefineryNodeLinkClick() {
    if(this.showMapnotsavedCnfrmDialog) {
      const dialogRef = this.confirmDialogService.openConfirmDialog('The Ingest Configuration has not been saved.<br>Leave page without saving?');
      const result = dialogRef.afterClosed().subscribe(result => {
        if(result) {
          this.router.navigate(['/node'], { state: { refineryConfigGuid: this.refineryConfigGuid } });
        }
      });
    } else {
      this.router.navigate(['/node'], { state: { refineryConfigGuid: this.refineryConfigGuid } });
    }
  }

  showSaveMappingConfigDialog(onClosed: (configName: string) => any ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {configName: this.viewModel.dataTransformConfigViewModel.name};

    const dialogRef = this.matDialog.open(SaveMappingConfigDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.name) { return; }
      // Name entered
      onClosed(result.name);
    });
  }

  deleteConfig(): void {
    this.showConfirmDialog('Delete config?', 'Are you sure you want to delete this config?', async (result) => {
      if (result) {
        try {
          if (await this.configService.deleteConfigByGuid(this.viewModel.config.guid)) {
            this.snackBar.open(`Successfully deleted config`, 'Dismiss', {
              duration: 5000
            });
            this.router.navigate(['/configs']);
          }
        } catch (err) {
          this.displayError('Failed to delete the config', 'Error', err);
        }
      }
    });
  }
  saveMappingConfig(): void {
    const timestampRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (this.viewModel?.config?.generalParameter.rawDataDefault && !timestampRegex.test(this.viewModel?.config?.generalParameter.rawDataDefault)) {
      this.displayError("In General Parameter 'Timestamp when empty' has invalid format. Use yyyy-MM-dd HH:mm:ss","Validation Eroor");
      return;
    }
    if (this.viewModel?.config?.generalParameter.timestampDefVal && !timestampRegex.test(this.viewModel?.config?.generalParameter.timestampDefVal)) {
      this.displayError("In General Parameter 'Timestamp' has invalid format. Use yyyy-MM-dd HH:mm:ss","Validation Eroor");
      return;
    }
  
    this.isSaveIngestDataBtnClicked = true;
    this.sharedService.sendEvent("save_ingestbtn_clicked");
    var busyDialog:any;
    this.saveConfigImpl(this.userData.getSelectedNodeDetails().name)
      .then(async (didSave) => {
        if (!didSave) { return; }
        if (this.rootStepper.selectedIndex === 1) {
          const node = this.userData.getSelectedNodeDetails();
          busyDialog = this.showBusyDialog('Running: ' + node?.descriptor);
          const runNodeRequest = new RunrefineryRequest(this.userData.getUserId(), this.userData.getOrgId(),node.refineryConfigGuid, node.id, this.refineryPresence.tabId);
          //busyDialog.componentInstance.message = 'Running: ' + node?.descriptor;
          //const runNodeResponse = await this.configService.runRefineryNode(runNodeRequest);
          const requestUrl = await this.configService.runRefineryNodeRequestUrl();
          var runNodeResponse = await this.runRefineryStream(runNodeRequest,requestUrl);
          this.sharedService.sendEvent("display_output_preview");
          this.getCacheStatus();
          
          // A run that failed ends with status fatal_error: its rows (if any) in the popup,
          // then the error itself -- it used to show only the popup, with no error (Fix B).
          const runOutcome = runNodeResponse as any;
          const runFailed = runOutcome?.status === 'fatal_error';
          const showFailure = () => {
            if (runFailed) { this.displayError(runOutcome?.message || 'The run failed.', 'Run failed'); }
          };
          if (runFailed && !(Array.isArray(runOutcome?.data) && runOutcome.data.length > 0)) {
            busyDialog.close();
            showFailure();
            return;
          }
          const dialogConfig = new MatDialogConfig();
          dialogConfig.minWidth = '450px';
          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.data = {
            runNodeResponse: runNodeResponse['data'],
            nodeName: node?.descriptor,
            farmTypeColor: '',
            runType: 'Node'
          };
          this.matDialog.open(RunNodeDialogComponent, dialogConfig)
            .afterClosed().subscribe(async () => {
              busyDialog.close();
              showFailure();
              // var configJson= await this.configService.getRefineryConfigByGuidAsync(this.userData.getRefineryId());
              // var confignode=configJson.nodes.find(node=>node.id==this.userData.getNodeId());
              // if (confignode.data.nodeStatus==NodeRunStatus.Completed) {
              //   if(confignode.data.isdbupdater){
              //     await this.runDataWriter();
              //   }
              // }
            });
        }else if (this.rootStepper.selectedIndex === 0) {
          busyDialog = this.showBusyDialog('Saving Ingest Node config');
          this.userData.setIngestId(this.ingestSaveConfig?.selectedIngestFarmGuid);
          if(this.ingestSaveConfig === undefined) {
            this.displayError("Column Map cannot be empty. Please map at least one column to the target.", "Column Map Error"); 
            return; 
          }
          const currentDataConnector = this.ingestSaveConfig.dataConnectorMappings
            .find(connector => connector.guid === this.ingestSaveConfig.selectedIngestFarmGuid)?.dataConnector;
  
          if (currentDataConnector?.databaseName) {
            const selectedIngestFarmGuid = this.viewModel?.config?.selectedIngestFarmGuid;
  
            if (!Array.isArray(this.viewModel?.config?.dataConnectorMappings)) {
              throw new Error("dataConnectorMappings is not an array or is undefined");
            }
  
            const matchedMapping = this.viewModel.config.dataConnectorMappings.find(
              mapping => mapping.guid === selectedIngestFarmGuid
            );
  
            const databaseType = matchedMapping?.dataConnector?.databaseType;
            const sqlCommand = matchedMapping?.dataConnector?.sqlCommand?.trim() || "";
  
            if (databaseType !== undefined && sqlCommand === "") {
              this.snackBar.open(`SQL Code is missing. Build the Query.`, 'Dismiss', {
                duration: 5000
              });
              throw new Error("SQL Command missing");
            }
          }
          
          if (currentDataConnector?.filePath || currentDataConnector?.databaseName || currentDataConnector?.partitionDirectory) {
            try {
              if (this.ingestSaveConfig.guid == undefined || this.ingestSaveConfig.guid == "") {
                this.ingestSaveConfig.guid = this.viewModel.dataTransformConfigViewModel.dataTransformerConfig?.guid
                this.ingestSaveConfig.modifiedDateTime = this.viewModel.dataTransformConfigViewModel.dataTransformerConfig?.modifiedDateTime;
              }
              // Column Mapper validation for the active Ingest Farm now happens earlier, in
              // saveConfigImpl before upsertConfig — so an unconfigured mapping is never saved.
              this.ingestSaveConfig.userId=this.userData.getUserId();
              this.ingestSaveConfig.orgId=this.userData.getOrgId();
              this.ingestSaveConfig.refineryId=this.userData.getRefineryId();
              this.ingestSaveConfig.nodeId=this.userData.getNodeId();
              this.ingestSaveConfig.transformId="";//this.userData.getTransformId();
              this.ingestSaveConfig.cacheKeyType=CacheKeyType.node;


              // var x=await this.configService.saevIngestFileData(this.ingestSaveConfig);
              // console.log("x:", x);
              const requestUrl = await this.configService.saevIngestFileDataUrl();
              await this.configService.streamPost<DataTransformerConfig, RunrefineryResponse>(
                requestUrl,
                this.ingestSaveConfig,
                (msg) => {
                  console.log("Progress message:", msg);
                }
              ).then(async result => {
                if (result){
                  console.log("Progress message result:", result);
                  if (result.message.includes("Error") && result.statusCode == 500){
                      throw new Error(result.message.toString())
                  } 
                  this.userData.setSelectedIngestMappingGUID("");
                  this.userData.setSelectedIngestMappingType(""); 
                }
              });
              busyDialog.close();
              this.snackBar.open(`Successfully Saved and Ingested Data.`, 'Dismiss', {
                duration: 6000
              });
          
              this.sharedService.sendEvent("ingestnode_saved_success");
          
            } catch (err) {
              const errorMessage = err?.error || err?.message || '';
              const statusCode = err?.status;
              if ( errorMessage.includes('No such file or directory')) {
                this.displayError("The file or file path of the Developer File Path was not found or incorrectly entered.", "File or File path not found");
              } else {
                if (errorMessage.includes('Error')){
                  this.displayWarning("Please check the Column Map for datatype mismatch or missing rows in numeric, Boolean or datetime fields",
                     errorMessage)
                } else {
                  this.displayError('Failed to ingest data. Please try again.', 'Error');
                }
                
              }
            }
          }
          else {
            this.snackBar.open(`Failed to Load Ingest farm. Data connector is not correct.`, 'Dismiss', {
              duration: 5000
            });
            throw new Error("Invalid data connector");
          }
          this.getCacheStatus(); 
        }
      })
      .catch(error => {
        console.error('Error in Saving Config:', error);
        this.displayError(`Error while saving`, 'Error', error);
      })
      .finally(() => {
        busyDialog?.close(); // ✅ Close the one dialog at the end
      });
  }

  private isBlankIngestConfig(ingestConfig: IngestConfigViewModel): boolean {
    return !ingestConfig.dataConnector && (!ingestConfig.columnMap || ingestConfig.columnMap.length === 0);
  }

  // targetDataFarm/refineryConfigGuid/nodeID/nodeType/nodeName are destination-specific — they
  // describe where THIS node's config lives, not the Ingest Farm's own settings — so they're left
  // out of the exported mapping and re-applied from the destination context on import instead.
  private buildDataConnectorMapping(ingestConfig: IngestConfigViewModel): DataConnectorMapping {
    return new DataConnectorMapping(
      ingestConfig.id,
      ingestConfig.name,
      null,
      ingestConfig.dataConnector?.toDataConnector(),
      ingestConfig.columnMap.map(i => i.toColumnMapDefinition()),
      ingestConfig.guid,
      ingestConfig.masterColumnMapGuid,
      undefined,
      undefined,
      undefined,
      undefined,
      ingestConfig.isMultiTabIngest,
      ingestConfig.excelTurboMode,
      ingestConfig.ingestAll,
      ingestConfig.sampleStart,
      ingestConfig.sampleEnd
    );
  }

  private getAllIngestConfigDataConnectorMappings(): DataConnectorMapping[] {
    return this.viewModel.dataTransformConfigViewModel.ingestConfigs.map(c => this.buildDataConnectorMapping(c));
  }

  exportIngestConfig(): void {
    const mappings = this.getAllIngestConfigDataConnectorMappings();
    if (mappings.length === 0) {
      this.displayError('Add an Ingest Farm before exporting.', 'Export Ingest Config');
      return;
    }
    const payload = {
      selectedIngestFarmGuid: this.viewModel.dataTransformConfigViewModel.selectedIngestFarmGuid,
      ingestConfigs: mappings
    };
    const content = JSON.stringify(payload, null, 2);
    const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.viewModel.dataTransformConfigViewModel.name || 'ingest_configs'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  copyIngestConfig(): void {
    const mappings = this.getAllIngestConfigDataConnectorMappings();
    if (mappings.length === 0) {
      this.displayError('Add an Ingest Farm before copying.', 'Copy Ingest Config');
      return;
    }
    const payload = {
      selectedIngestFarmGuid: this.viewModel.dataTransformConfigViewModel.selectedIngestFarmGuid,
      ingestConfigs: mappings
    };
    this.clipboard.copy(JSON.stringify(payload, null, 2));
    this.snackBar.open('Ingest configs copied to clipboard', 'Close', {
      duration: 2000
    });
  }

  private parseIngestConfigExport(parsed: any): { selectedIngestFarmGuid?: string; mappings: DataConnectorMapping[] } {
    if (Array.isArray(parsed)) {
      return { mappings: parsed.map(item => DataConnectorMapping.fromJSON(item)) };
    }
    if (parsed?.ingestConfigs) {
      return {
        selectedIngestFarmGuid: parsed.selectedIngestFarmGuid,
        mappings: parsed.ingestConfigs.map((item: any) => DataConnectorMapping.fromJSON(item))
      };
    }
    return { mappings: [DataConnectorMapping.fromJSON(parsed)] };
  }

  async importIngestConfig(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];
    try {
      const text = await file.text();
      const imported = this.parseIngestConfigExport(JSON.parse(text));
      this.confirmReplaceIngestConfigs(imported.mappings, imported.selectedIngestFarmGuid);
    } catch (err) {
      this.displayError('Failed to import ingest config', 'Error', err);
    } finally {
      input.value = '';
    }
  }

  pasteIngestConfig(): void {
    const dialogRef = this.matDialog.open(JsonPasteDialogComponent, {
      width: '600px',
      height: '420px',
      disableClose: true,
      data: { inputType: 'json' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) { return; }
      try {
        const imported = this.parseIngestConfigExport(result);
        this.confirmReplaceIngestConfigs(imported.mappings, imported.selectedIngestFarmGuid);
      } catch (err) {
        this.displayError('Failed to paste ingest config', 'Error', err);
      }
    });
  }

  private confirmReplaceIngestConfigs(importedMappings: DataConnectorMapping[], selectedIngestFarmGuid?: string): void {
    if (!importedMappings || importedMappings.length === 0) {
      this.displayError('No ingest config found to import.', 'Import Ingest Config');
      return;
    }

    const selectedNode = this.userData.getSelectedNodeDetails();

    this.showConfirmDialog(
      "Replace this existing Ingest Node's configuration?",
      '',
      (confirmed) => {
        if (!confirmed) { return; }

        const dataTransformConfigViewModel = this.viewModel.dataTransformConfigViewModel;
        const previousSelectedGuid = dataTransformConfigViewModel.selectedIngestFarmGuid;
        let guidToSelect = selectedIngestFarmGuid;

        // Upsert every imported Ingest Farm by guid: an existing farm gets its connector/columnMap/
        // settings replaced in place (identity + destination targetDataFarm preserved); a farm with
        // no guid match didn't exist on this node yet, so it's appended as a new Ingest Farm — its
        // targetDataFarm is left unset, same as any other newly added Ingest Farm.
        importedMappings.forEach(importedMapping => {
          importedMapping.refineryConfigGuid = selectedNode.refineryConfigGuid;
          importedMapping.nodeID = selectedNode.id;
          importedMapping.nodeName = selectedNode.name;
          importedMapping.nodeType = selectedNode.nodeboxtypeid.toString();

          const index = dataTransformConfigViewModel.ingestConfigs.findIndex(c => c.guid === importedMapping.guid);
          if (index >= 0) {
            const existing = dataTransformConfigViewModel.ingestConfigs[index];
            const replacement = new IngestConfigViewModel(importedMapping, existing.columns, []);
            replacement.guid = existing.guid;
            replacement.id = existing.id;
            replacement.name = existing.name;
            replacement.transforms = existing.transforms;
            replacement.targetDataFarm = existing.targetDataFarm;
            dataTransformConfigViewModel.ingestConfigs[index] = replacement;
          } else {
            // No guid match — this Ingest Farm doesn't exist on this node yet. If there's still an
            // untouched placeholder farm (e.g. the auto-created "Default" farm on a brand new node,
            // never given a connector), take over its slot instead of leaving it behind — otherwise
            // the Data Connector dropdown ends up with two "Default" entries after import.
            const blankIndex = dataTransformConfigViewModel.ingestConfigs.findIndex(c => this.isBlankIngestConfig(c));
            if (blankIndex >= 0) {
              const blankGuid = dataTransformConfigViewModel.ingestConfigs[blankIndex].guid;
              const replacement = new IngestConfigViewModel(importedMapping, [], []);
              dataTransformConfigViewModel.ingestConfigs[blankIndex] = replacement;
              // The placeholder's guid is gone now — if that's what was selected, follow it to the
              // farm that took its place, or the dropdown/mapper are left pointing at nothing.
              if (!guidToSelect && previousSelectedGuid && blankGuid === previousSelectedGuid) {
                guidToSelect = replacement.guid;
              }
            } else {
              dataTransformConfigViewModel.ingestConfigs.push(new IngestConfigViewModel(importedMapping, [], []));
            }
          }
        });

        if (guidToSelect && dataTransformConfigViewModel.ingestConfigs.some(c => c.guid === guidToSelect)) {
          dataTransformConfigViewModel.selectedIngestFarmGuid = guidToSelect;
        }

        // The connector step (app-import-data) is OnPush and reads its @Input ingestConfigViewModel
        // by reference, so mutating the existing object's fields in place leaves it stale until an
        // unrelated click happens to mark it dirty. A new DataTransformConfigViewModel reference is
        // pushed through the shared service so IngestWorkFlowLayoutComponent reselects it and the
        // connector view re-renders immediately.
        const refreshedViewModel = Object.assign(
          Object.create(Object.getPrototypeOf(dataTransformConfigViewModel)),
          dataTransformConfigViewModel
        );
        this.configViewModelService.setDataTransformConfigViewModel(refreshedViewModel);

        this.sharedService.sendEvent('mapper_updated');
        if (dataTransformConfigViewModel.selectedIngestFarmGuid) {
          this.sharedService.sendEvent({ type: 'select_ingest_farm', guid: dataTransformConfigViewModel.selectedIngestFarmGuid });
        }
        this.snackBar.open('Ingest configs imported. Review and Save & Ingest Data to apply.', 'Dismiss', {
          duration: 5000
        });
      },
      'Replace',
      'warning-dialog'
    );
  }

  /**
   * Stores the ingest file browser's Home path for one data connector type in the
   * refinery's ingestHomePaths (keeping the other connector types' entries) and
   * saves the refinery via UpsertRefineryConfig(). Uses the same refinery object the
   * node Save upserts (history.state.refineryConfig), so UpsertRefineryConfig()
   * advances its revision in place and a later node Save neither conflicts with
   * nor drops the Home path. Nodes, rcNo/rowNo/colNo and farm types are untouched.
   */
  private async saveIngestHomePath(dataConnectorType: number, path: string): Promise<void> {
    const refineryConfig: DataRefineryConfig = history.state.refineryConfig ?? this.userData.getSelectedRefineryDetails();
    if (!refineryConfig?.guid || dataConnectorType == null || !path) {
      this.displayError('The Home path could not be saved because no refinery is loaded.', 'Set to Home');
      return;
    }

    const previousHomePaths = refineryConfig.ingestHomePaths;
    refineryConfig.ingestHomePaths = { ...(previousHomePaths ?? {}), [String(Number(dataConnectorType))]: path };
    try {
      await this.configService.UpsertRefineryConfig(refineryConfig);
      this.snackBar.open('Home path set.', 'Dismiss', { duration: 3000 });
    } catch (err: any) {
      // Not saved: don't leave the unsaved value for a later save to persist.
      refineryConfig.ingestHomePaths = previousHomePaths;
      this.displayError(err?.message || 'Failed to set home path', 'Set to Home', err);
    }
  }

  async saveIngest(event:string){
    await this.saveConfigImpl(event) ;
  }
  prevConfig:any;
  async saveConfigImpl(configName: string = this.userData.getSelectedNodeDetails().name): Promise<boolean> {
    
    // const busyDialog = this.showBusyDialog('Saving...n');
    try {
      this.viewModel.saveInProgress = true;

      const configJsonObject = this.userData.getSelectedConfigJson(); //state.config;
      if(configJsonObject){
        let selectedIngestFarmID = this.userData.getSelectedIngestFarmGUID();
        const configs = this.viewModel.dataTransformConfigViewModel.ingestConfigs;
       let selectedIndex: number = configs.findIndex(f => f.guid === selectedIngestFarmID);
        let newIndex: number;

        // No farms left
        if (configs.length === 0) {
           // console.warn('⚠️ No ingest configs left, cannot save.');
           // console.groupEnd();
            return false;
        }

        if (selectedIndex < 0) {
            // Previously selected farm was deleted or missing → pick the last farm
            newIndex = configs.length - 1;
        } else if (selectedIndex === 0) {
            // Deleted first farm → select the new first farm
            newIndex = 0;
        } else if (selectedIndex >= configs.length) {
            // Deleted last farm → select the new last farm
            newIndex = configs.length - 1;
        } else {
            // Deleted a middle farm → select the next farm at same index
            newIndex = selectedIndex;
        }

        // Safety clamp
        newIndex = Math.max(0, Math.min(newIndex, configs.length - 1));

    selectedIngestFarmID = configs[newIndex].guid;
    this.viewModel.dataTransformConfigViewModel.selectedIngestFarmGuid = selectedIngestFarmID;
    this.userData.setSelectedIngestFarmGUID(selectedIngestFarmID);

        let ingestFarmConfig = this.viewModel.dataTransformConfigViewModel.ingestConfigs.filter(ingestConfig => ingestConfig.guid == selectedIngestFarmID)[0]
      if (!ingestFarmConfig) {
        return false; // Stop saving if GUID not found
      }
        const emptyColMapStatus = ingestFarmConfig.columnMap.every(item => item.targetColumnName === '');
        if(emptyColMapStatus) {
          let prevColMappings = configJsonObject.dataConnectorMappings.filter(dataconnectormapping => dataconnectormapping.guid == selectedIngestFarmID)[0].columnMap
          // Loop through current columnMap and update if there's a matching sourceColumn
          ingestFarmConfig.columnMap.forEach(colmap => {
              const match = prevColMappings.find(prev => prev.sourceColumn === colmap.columnName);
              if (match) {
                colmap.targetColumnName = match.targetColumn;
                colmap.dataType = match.dataType;
                colmap.pyDataType = match.pyDataType;
                colmap.id = match.id;
              }
           });          
        } 
      }

      let config = this.viewModel.config ?? new DataTransformerConfig();
      config.name = configName;
      config.selectedIngestFarmGuid = this.viewModel.dataTransformConfigViewModel.selectedIngestFarmGuid;
      config.organizationId = 1;
      const selectedNode=this.userData.getSelectedNodeDetails();
      config.dataConnectorMappings = this.viewModel.dataTransformConfigViewModel.ingestConfigs
        .map ( ingestConfig => 
          new DataConnectorMapping(
            ingestConfig.id,
            ingestConfig.name,
            ingestConfig.targetDataFarm,
            ingestConfig.dataConnector?.toDataConnector(),
            ingestConfig.columnMap.map(i => i.toColumnMapDefinition()),
            ingestConfig.guid,
            ingestConfig.masterColumnMapGuid,
            selectedNode.refineryConfigGuid,
            selectedNode.id,
            selectedNode.name, 
            selectedNode.nodeboxtypeid.toString(),
            ingestConfig.isMultiTabIngest,
            ingestConfig.excelTurboMode,
            ingestConfig.ingestAll,
            ingestConfig.sampleStart,
            ingestConfig.sampleEnd
          )
        );
      
        config.dataTypeConverter = this.viewModel.dataTransformConfigViewModel.DataTypeConverter;
        config.durationBuilder = this.viewModel.dataTransformConfigViewModel.DurationBuilder;
        config.deduplicateRows = this.viewModel.dataTransformConfigViewModel.DeduplicateRows;
        config.generalParameter = this.viewModel.dataTransformConfigViewModel.GeneralParameter;

      this.selectedIngestFarmGUID = config.selectedIngestFarmGuid;//this.userData.getSelectedIngestFarmGUID(); // Fetch the saved/updated selected IngestFarmGUID from the local storage.
      const selectedIngestFarmIndex = config.dataConnectorMappings.findIndex(dataConnectrMap => dataConnectrMap.guid == this.selectedIngestFarmGUID);   
    
      // Save the transforms
      config.transforms = this.viewModel.dataTransformConfigViewModel.transforms.map(t => t.transform);
      this.ingestSaveConfig = config

      // Save the ingest transforms
      this.DataConnectorforRun = config.dataConnectorMappings;

      // Validate the Column Mapper for the active Ingest Farm before persisting anything —
      // must run before upsertConfig so an unconfigured/stale mapping is never saved.
      const activeIngestFarmMapping = config.dataConnectorMappings
        ?.find(connector => connector.guid === config.selectedIngestFarmGuid);
      if (activeIngestFarmMapping && activeIngestFarmMapping.columnMap.length === 0) {
        const ingestFarmGuid = activeIngestFarmMapping.guid;
        this.displayWarning(
          "Please configure the Column Mapper before saving.",
          "Column Mapper Not Configured",
          null,
          () => {
            this.sharedService.sendEvent({ type: 'open_mapper_panel', guid: ingestFarmGuid });
          }
        );
        return false;
      }

      config = await this.configService.upsertConfig(config);
      this.userData.setSelectedIngestFarmGUID(config.selectedIngestFarmGuid);
      this.userData.setIngestId(config.selectedIngestFarmGuid);
      this.allNewConfigAfterSave = config;

      this.configViewModelService.setDataTransformConfigViewModel(new DataTransformConfigViewModel(config));

      // Save the configGuid in Refinery config all state coming from node defination page please do not do anything
      if(history.state.refineryConfig){
        var refineryNodeConfig=history.state.refineryConfig.nodes.find(x => x.id == history.state.nodeId);
        refineryNodeConfig.data.ConfigGuid=config.guid;
        var refineryConfig = await this.configService.UpsertRefineryConfig(history.state.refineryConfig);
      }

      this.cdRef.detectChanges();
      // this.snackBar.open(`Running all the Transformers of this Node. `, 'Dismiss', {
      //   duration: 5000
      // });
      return true;
    } catch (err) {
      this.displayError(`Failed to save '${configName}'`, 'Error', err);
      return false;
    } finally {
      this.viewModel.saveInProgress = false;
      // busyDialog.close();
    }
  }
  
  async runAllTransformbyLast(
    filters?: ColumnFilterInfo[],
    outputFarmFilters?: ColumnFilterInfo[],
    showDialog: boolean = true,
    showCompletionMessage: boolean = true): Promise<void> {
    
    //   if (!this.validate()) {
    //   return;
    // }

    this.viewModel.isTransformRunning = true;

    const minDisplayEndTime = new Date(Date.now() + this.minDialogDisplayTimeMs);
    const pipelineVm = new TransformPipelineViewModel();
    // const busyIndicator = showDialog ? this.showPipelineDialog(pipelineVm, 'Executing transforms...') : null;
    const busyIndicator = null;
    const busyIndicatorComp = busyIndicator?.componentInstance;  // hold on to reference or else will be null after it is closed

    try {
     var nodeID=this.userData.getSelectedNodeDetails().id;
     var refineryID=this.userData.getSelectedNodeDetails().refineryConfigGuid;

      var allTransformers = this.allNewConfigAfterSave.transforms.filter(x=>x.nodeID === nodeID);
      allTransformers.sort((a, b) => b.index - a.index);

      this.allTransformerConfigforpipeline = []

      allTransformers.forEach((OneTransform) => {
        const transform1 = new TransformerViewModel(OneTransform);
        this.allTransformerConfigforpipeline.push(transform1);
      });

      // We need to build the transform pipeline
      // and execute each transform starting from the top
      const pipeline = this.transformPipelineService.buildPipeline(
        allTransformers[0].guid,
        this.allTransformerConfigforpipeline.filter(x=>x.nodeID === nodeID));
      // Build view Model of Transform Pipeline
      pipelineVm.items = pipeline.groupedTransforms.map(g => {
        const group = new GroupedTransformPipelineItem();
        group.groupName = g.groupName;
        group.items = g.transforms.map(t => {
          return new TransformPipelineItem(t.name, g.groupName, 'Pending', t, t.guid === allTransformers[0].guid,"","");
        });
        return group;
      }
      );
      const dataConnectors = this.DataConnectorforRun ;
      // Get the active input ingest data farm (data connector)
      // and ensure every transform runs with the correct one
      const activeDataConnector = dataConnectors.find(d => d.guid === this.allNewConfigAfterSave.selectedIngestFarmGuid);
      if (!activeDataConnector) {
        throw new Error(`Active Data Connector not found!  Id = ${this.allNewConfigAfterSave.selectedIngestFarmGuid}`);
      }
      let didFail = false;
      // Extract all values of the source column of Data
      var parentNodes = this.userData.getNodeParentDataLine();
      var AttrNodes = this.userData.getNodeAttributionLine();
      let transformerRunType = "transformer_prev"
      const activeTransformGuid = this.userData.getSelectedTransform().guid
      // Execute the pipeline
      const runconfigStatus=await this.transformPipelineService.executePipelineAsyncOld(
        refineryID,
        parentNodes,
        AttrNodes,
        pipeline,
        dataConnectors,
        transformerRunType,
        activeTransformGuid,
        activeDataConnector.guid, (transform, groupName) => {
          pipelineVm.currentGroup = groupName;

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transform)[0];

          // Update the active source data farm, even though it's not the first one
          // this should make the transform dirty so it runs again
          // as the root input farm has changed.
          // TODO: Perhaps we us an isDirty or invalidated property instead?
          item.isRunning = true;
          item.statusDisplayText = 'Executing...';
        },
        (request) => {
          // Each pipeline step runs a different transform — read the compiled plan off
          // that specific transform itself, not a single config-wide value.
          const compiledConfig = (request.transforms?.[0] as any)?.compiledConfig;
          if (compiledConfig) { request.compiledConfig = compiledConfig; }
        },
        (transformerViewModel, groupName, success, response, err) => {
          didFail = !success && err != null;

          if (busyIndicatorComp) {
            busyIndicatorComp.hasError = didFail;
          }

          const item = pipelineVm.items
            .filter(g => g.groupName === groupName)[0]
            .items.filter(t => t.transform === transformerViewModel)[0];
          item.isRunning = false;
          item.statusDisplayText = success ? 'Completed!' : `Failed!`;

          if (transformerViewModel.guid === allTransformers[0].guid && response != null) {
            this.previewTable.bindData(response.activeData, 'No Results');
            this.outputDataFarmPreviewTable.bindData(response.currentData, 'No Farm Data');

            if (showCompletionMessage) {
              this.snackBar.open(`Transform results have been updated!`, 'Dismiss', {
                duration: 3000
              });
            }

            this.viewModelBase.onTransformRunCompleted(response as RowExtractorPreviewDataResponse);
          }
        }, transformerViewModel => {

          // Ensure we set Transient runAll property to true for all pre transforms so full set of data is processed 
          transformerViewModel.transform.setRunAll(true);

          return null;
          
      });

      return new Promise<void>((resolve, reject) => {
        // Resolve the promise when the operation is successful
        resolve();
      });
    } catch (err) {
      this.displayError('Failed to run transform', 'Error', err);
    } finally {
      const diff = Date.now() - minDisplayEndTime.getTime();

      this.viewModel.isTransformRunning = false;

      return new Promise<void>((resolve, reject) => {
        // Resolve the promise when the operation is successful
        resolve();
      });

    }
    
  }

  async saveTheOutputFile(NodeDetails): Promise<void>{
    var result=await this.configService.saveProcessorNode(NodeDetails);
    return new Promise<void>((resolve, reject) => {
      // Resolve the promise when the operation is successful
      resolve();
    });
  }
  async ResetNode(): Promise<any> {
    var nodeId=this.userData.getNodeId();
    const busyDialog = this.showBusyDialog('Resetting node...');

    const confirmationResult = await new Promise<boolean>((resolve) => {
      this.showConfirmDialog(
        'Reset Node  will reset the node to the Idle state !',
        ' ',
        (result) => resolve(result)
      );
    });
    if (!confirmationResult) {
      busyDialog.close();
      return;
    }  
    var runNodeRequest: RunrefineryRequest = new RunrefineryRequest(this.userData.getUserId(), this.userData.getOrgId(),this.userData.getRefineryId(),nodeId, this.refineryPresence.tabId);
    try {
      const resetRefineryResponse = await this.configService.resetNodeRefinery(runNodeRequest);
      console.log("resetRefineryResponse:", resetRefineryResponse);
      this.snackBar.open(`Node has been reset successfully.`, 'Dismiss', {
        duration: 5000
      });
      this.sharedService.sendEvent("reset_node_successfully");
      this.getCacheStatus();
      // The Architect, 2026-09-29: sendEvent() is fire-and-forget -- the Node
      // table's own "reset_node_successfully" subscriber (transformer-list.component.ts)
      // waits this exact 1000ms before it even starts refreshing, then does its own
      // async work on top of that. Without this wait here too, the spinner closed
      // before the table had even begun updating, making a successful reset look
      // like it did nothing. Matches the existing delay already used for this same
      // event, rather than inventing a new one.
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch(err) {
        this.displayError('Failed to reset the refinery', 'Error', err);
    } finally {
        busyDialog.close()
    }
  }
  async getCacheStatus(): Promise<void> {
    try {
      const request = new CacheStatusRequest(
        this.userData.getUserId(),
        this.userData.getOrgId(),
        this.userData.getRefineryId(),
        this.userData.getNodeId(),
        "",
        CacheKeyType.node);
      const response = await this.configService.getCacheStatus(request);
      if(response){
        this.cacheStatus=response.cacheStatus
      }
      
    }catch (err) {
      this.displayError('Failed to Update Node Status', 'Error', err);
    } finally {
      //sender.isGettingData.next(false);
    }
  }  
  async runDataWriter() {
    const busyIndicator = this.showBusyDialog('Please wait running data writer...');
    try {
        var tws = new TestWriterRequest();
        const node = this.userData.getSelectedNodeDetails();
        tws.nodeID = node.id;
        tws.refineryConfigID = node.refineryConfigGuid;

        tws.userId = this.userData.getUserId();
        tws.orgId = this.userData.getOrgId();
        tws.refineryId = this.userData.getRefineryId();
        tws.nodeId = this.userData.getNodeId();
        tws.transformId = "";
        tws.tabId = this.refineryPresence.tabId;
        tws.cacheKeyType = CacheKeyType.node;


        var testwriter = await this.configService.testWriter(tws);
        
        if (testwriter.status === "Completed") {
            this.snackBar.open(testwriter.message, 'Dismiss', {
                duration: 5000
            });
        } else {
            this.displayError(`Failed to write data writer`, 'Error', testwriter.message);
        }

        busyIndicator.close();
    } catch (err) {
        busyIndicator.close();
        this.displayError(`Failed to write data writer`, 'Error', err);
    }
  }
}
